package ai.jessie.web.pages.organisations

import ai.jessie.shared.customers.CustomerResponse
import ai.jessie.web.customers.CustomerState
import ai.jessie.web.customers.CustomerUiState
import ai.jessie.web.customers.LIST_TITLE
import ai.jessie.web.customers.LocalCustomerPort
import ai.jessie.web.customers.customerHeaderActions
import ai.jessie.web.customers.customerRecords
import ai.jessie.web.customers.primaryAddressLine
import ai.jessie.web.onboarding.OnboardingProblem
import ai.jessie.web.onboarding.onboardingProblem
import ai.jessie.web.onboarding.organisationRequiredProblem
import ai.jessie.web.onboarding.validOrganisationId
import ai.jessie.web.theme.AlertStyle
import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.appFrame
import ai.jessie.web.theme.crumb
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.key
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.silk.style.toAttrs
import org.jetbrains.compose.web.dom.Div

// UC-012. Tenant state is keyed by the routed organisation, so a change of organisation discards
// it together with any unsaved proposal.
@Page("{organisationId}/customers")
@Composable
fun organisationCustomersPage(ctx: PageContext) {
    val organisationId = ctx.route.dynamicParams["organisationId"].orEmpty()
    if (!validOrganisationId(organisationId)) {
        customerProblemFrame(organisationId, organisationRequiredProblem())
    } else key(organisationId) { customersContent(organisationId, null) }
}

/** The customer frame around a problem that needs no request, e.g. an invalid link. */
@Composable
internal fun customerProblemFrame(
    organisationId: String,
    problem: OnboardingProblem,
    title: String = LIST_TITLE,
) {
    appFrame(organisationId, customersHref(organisationId), title) {
        Div(attrs = AlertStyle.toAttrs()) { onboardingProblem(problem, busy = false) }
    }
}

@Composable
internal fun customersContent(organisationId: String, customerId: String?) {
    val port = LocalCustomerPort.current
    val scope = rememberCoroutineScope()
    val model =
        remember(port, scope, organisationId, customerId) {
            CustomerState(port, scope, organisationId, customerId)
        }
    LaunchedEffect(model) { if (!AppGlobals.isExporting) model.refresh() }
    val state = model.state
    val authenticationRequired = state.error?.authenticationRequired == true
    // The detail page is titled by the returned record; the list keeps its name.
    val record = state.current?.takeIf { customerId != null }
    val actionable = state.draft == null && !authenticationRequired
    appFrame(
        organisationId,
        customersHref(organisationId),
        when {
            customerId == null -> LIST_TITLE
            record != null -> record.displayName
            else -> DETAIL_TITLE
        },
        subtitle = record?.primaryAddressLine(),
        authenticationRequired = authenticationRequired,
        crumb =
            if (customerId != null) {
                {
                    crumb(
                        LIST_TITLE,
                        customersHref(organisationId),
                        record?.displayName ?: DETAIL_TITLE,
                    )
                }
            } else null,
        actions = customerActions(customerId, record, state, model, actionable),
    ) {
        customerRecords(organisationId, customerId, state, model.actions)
    }
}

// List: the primary create action while the list is shown. Detail: edit and lifecycle actions.
private fun customerActions(
    customerId: String?,
    record: CustomerResponse?,
    state: CustomerUiState,
    model: CustomerState,
    actionable: Boolean,
): (@Composable () -> Unit)? =
    when {
        !actionable -> null
        customerId == null && state.customers != null && state.current == null -> {
            {
                themedButton(
                    "Firma oder Person",
                    model.actions.create,
                    variant = ButtonVariant.PRIMARY,
                    disabled = state.busy,
                )
            }
        }
        record != null -> {
            { customerHeaderActions(record, state.busy, model.actions) }
        }
        else -> null
    }

internal fun customersHref(organisationId: String) = "/organisations/$organisationId/customers"

private const val DETAIL_TITLE = "Firma oder Person"
